import React, { useState, useEffect } from "react";
import { api, formatErr, buildWhatsAppLink } from "../lib/api";
import { toast } from "sonner";
import { MessageCircle, Send } from "lucide-react";

const QUANTITIES = ["1 kg", "5 kg", "10 kg", "50 kg", "100 kg", "100+ kg (Quote)"];
const GRADES = ["Not sure / Recommend me", "Premium Grade", "Signature Grade", "Signature Liquid Flavour"];

export default function BulkEnquiryForm({ compact = false, onSuccess, initialGrade = "" }) {
  const [form, setForm] = useState({
    name: "",
    contact: "",
    email: "",
    quantity: "5 kg",
    interested_grade: initialGrade || GRADES[0],
    company: "",
    message: "",
  });
  const [submitting, setSubmitting] = useState(false);

  // Pre-select grade if dialog opened with a grade context
  useEffect(() => {
    if (initialGrade) setForm((f) => ({ ...f, interested_grade: initialGrade }));
  }, [initialGrade]);

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const buildWaText = () => {
    return `Hi I'm Ube team! 👋\n\nI'd like a bulk wholesale enquiry for Ube Powder:\n• Name: ${form.name}\n• Contact: ${form.contact}\n• Email: ${form.email}\n• Quantity: ${form.quantity}\n• Interested Grade: ${form.interested_grade}\n${form.company ? `• Company: ${form.company}\n` : ""}${form.message ? `• Notes: ${form.message}` : ""}`;
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.contact || !form.email || !form.quantity) {
      toast.error("Please fill name, contact, email and quantity.");
      return;
    }
    setSubmitting(true);
    try {
         await fetch("https://formspree.io/f/maqvzlrd", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(form)
    });
    toast.success("Enquiry received! We will email you shortly.");
    setForm({ name: "", contact: "", email: "", quantity: "5 kg", interested_grade: initialGrade || GRADES[0], company: "" });
    onSuccess?.();
      toast.error(formatErr(err.response?.data?.detail) || "Failed. Please try WhatsApp.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
  <form onSubmit={submit} name="Bulk-Enquiry" className={`grid ${compact ? "grid-cols-1" : "grid-cols-1 md:grid-cols-2"} gap-4`} data-testid="bulk-enquiry-form">
      <div className={compact ? "" : "md:col-span-1"}>
        <label className="block text-xs uppercase tracking-[0.18em] text-[#666] mb-2">Name *</label>
        <input
          name="name"
          value={form.name}
          onChange={(e) => update("name", e.target.value)}
          placeholder="Your full name"
          className="w-full bg-white border border-[#E5E0D8] rounded-lg px-4 py-3 focus:ring-2 focus:ring-[#4B0082] focus:border-transparent outline-none"
          data-testid="enquiry-name"
        />
      </div>
      <div>
        <label className="block text-xs uppercase tracking-[0.18em] text-[#666] mb-2">Contact No. *</label>
        <input
          name="contact"
          value={form.contact}
          onChange={(e) => update("contact", e.target.value)}
          placeholder="+91..."
          className="w-full bg-white border border-[#E5E0D8] rounded-lg px-4 py-3 focus:ring-2 focus:ring-[#4B0082] focus:border-transparent outline-none"
          data-testid="enquiry-contact"
        />
      </div>
      <div>
        <label className="block text-xs uppercase tracking-[0.18em] text-[#666] mb-2">Email *</label>
        <input
          name="email"
          type="email"
          value={form.email}
          onChange={(e) => update("email", e.target.value)}
          placeholder="you@business.com"
          className="w-full bg-white border border-[#E5E0D8] rounded-lg px-4 py-3 focus:ring-2 focus:ring-[#4B0082] focus:border-transparent outline-none"
          data-testid="enquiry-email"
        />
      </div>
      <div>
        <label className="block text-xs uppercase tracking-[0.18em] text-[#666] mb-2">Quantity *</label>
        <select
          value={form.quantity}
          onChange={(e) => update("quantity", e.target.value)}
          className="w-full bg-white border border-[#E5E0D8] rounded-lg px-4 py-3 focus:ring-2 focus:ring-[#4B0082] focus:border-transparent outline-none"
          data-testid="enquiry-quantity"
        >
          {QUANTITIES.map((q) => <option key={q} value={q}>{q}</option>)}
        </select>
      </div>
      <div className={compact ? "" : "md:col-span-2"}>
        <label className="block text-xs uppercase tracking-[0.18em] text-[#666] mb-2">Interested Grade *</label>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-2" data-testid="enquiry-grade-group">
          {GRADES.map((g) => (
            <button
              type="button"
              key={g}
              onClick={() => update("interested_grade", g)}
              className={`text-xs md:text-sm px-3 py-2.5 rounded-lg border transition-colors text-left ${
                form.interested_grade === g
                  ? "bg-[#4B0082] text-white border-[#4B0082]"
                  : "bg-white text-[#1A1A1A] border-[#E5E0D8] hover:border-[#4B0082]"
              }`}
              data-testid={`enquiry-grade-${g.split(" ")[0].toLowerCase()}`}
            >
              {g}
            </button>
          ))}
        </div>
      </div>
      <div className={compact ? "" : "md:col-span-2"}>
        <label className="block text-xs uppercase tracking-[0.18em] text-[#666] mb-2">Company / Brand (optional)</label>
        <input
          value={form.company}
          onChange={(e) => update("company", e.target.value)}
          placeholder="Cafe / Bakery / Brand name"
          className="w-full bg-white border border-[#E5E0D8] rounded-lg px-4 py-3 focus:ring-2 focus:ring-[#4B0082] focus:border-transparent outline-none"
          data-testid="enquiry-company"
        />
      </div>
      <div className={compact ? "" : "md:col-span-2"}>
        <label className="block text-xs uppercase tracking-[0.18em] text-[#666] mb-2">Tell us more</label>
        <textarea
          rows={4}
          value={form.message}
          onChange={(e) => update("message", e.target.value)}
          placeholder="Anything specific — packaging, delivery city, frequency, sample requirement..."
          className="w-full bg-white border border-[#E5E0D8] rounded-lg px-4 py-3 focus:ring-2 focus:ring-[#4B0082] focus:border-transparent outline-none resize-none"
          data-testid="enquiry-message"
        />
      </div>
      <div className={`${compact ? "" : "md:col-span-2"} flex flex-col sm:flex-row gap-3 mt-2`}>
        <button type="submit" disabled={submitting} className="btn-primary justify-center disabled:opacity-60" data-testid="enquiry-submit">
          <Send size={16} /> {submitting ? "Sending..." : "Send Enquiry"}
        </button>
        <a
          href={buildWhatsAppLink(buildWaText())}
          target="_blank"
          rel="noreferrer"
          className="btn-gold justify-center"
          data-testid="enquiry-whatsapp"
        >
          <MessageCircle size={16} /> Send via WhatsApp
        </a>
      </div>
      <p className={`${compact ? "" : "md:col-span-2"} text-xs text-[#888]`}>
        By submitting you agree to be contacted by I'm Ube for your enquiry. We respect your privacy — no spam, ever.
      </p>
    </form>
  );
}
